import { useState, type CSSProperties, type FC, type ReactNode } from 'react';
import { V4 } from '../tokens';

// Barra sticky de comparación (peek): se muestra mientras el comercio está "mirando la versión
// anterior" sin haberla rechazado (compare_active=true). Durante el peek la página resuelta es
// V1 (control), así que esta barra es el ancla de retorno: "Volver a la nueva".
//
// Posición: sticky arriba en desktop, fija abajo en móvil (pulgar). Se monta en el shell del
// menú (MenuVersionInvitation), sobre el contenido. Self-contained, 100% inline styles V4.
//
// El retorno lo dispara SOLO el root; para no-root mostramos un aviso suave (no rompemos).

export interface ComparePeekBarProps {
  // Vuelve a la versión nueva (switchTo('treatment', 'compare_return')). Lo provee el consumidor.
  onReturn: () => void;
  isRoot: boolean;
}

const EyeIcon: ReactNode = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const ForwardIcon: ReactNode = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    aria-hidden="true"
  >
    <path d="M13 5l7 7-7 7M20 12H4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Wrapper sticky: el media query no aplica a inline styles, así que detectamos el viewport
// una vez al render con matchMedia (sin listener: el shell se re-monta al navegar).
function isMobileViewport(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(max-width: 640px)').matches;
  } catch {
    return false;
  }
}

const returnButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  background: V4.secondary,
  color: V4.white,
  border: 0,
  borderRadius: 8,
  padding: '8px 14px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

export const ComparePeekBar: FC<ComparePeekBarProps> = ({ onReturn, isRoot }) => {
  const [softNotice, setSoftNotice] = useState<string | null>(null);
  const mobile = isMobileViewport();

  const barStyle: CSSProperties = {
    position: mobile ? 'fixed' : 'sticky',
    ...(mobile ? { left: 12, right: 12, bottom: 12 } : { top: 0 }),
    zIndex: 50,
    marginBottom: mobile ? 0 : 12,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    background: V4.grayBlueLight,
    border: `1px solid ${V4.secondaryLight2}`,
    borderRadius: 10,
    padding: '10px 14px',
    boxShadow: mobile ? '0 8px 24px rgba(37,55,70,.18)' : '0 2px 8px rgba(37,55,70,.08)',
    fontFamily: 'inherit',
  };

  const handleReturn = () => {
    if (!isRoot) {
      setSoftNotice('Solo el administrador principal (root) puede cambiar la versión de este comercio.');
      return;
    }
    setSoftNotice(null);
    onReturn();
  };

  return (
    <div style={barStyle} role="region" aria-label="Comparando versiones del menú">
      <span
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: V4.white,
          color: V4.secondary,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {EyeIcon}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: V4.primary }}>
          Estás comparando con la versión anterior
        </span>
        <span style={{ display: 'block', fontSize: 11.5, color: V4.grayUltraDark, marginTop: 1, lineHeight: 1.4 }}>
          Cuando termines, vuelve a la nueva — no perdiste nada.
        </span>
      </span>
      <button type="button" style={returnButton} onClick={handleReturn}>
        {ForwardIcon}
        Volver a la nueva
      </button>
      {softNotice && (
        <span
          style={{
            flexBasis: '100%',
            padding: '8px 10px',
            borderRadius: 8,
            background: V4.warningFaded,
            color: V4.warningDark,
            fontSize: 11.5,
            fontWeight: 600,
            lineHeight: 1.4,
          }}
        >
          {softNotice}
        </span>
      )}
    </div>
  );
};

export default ComparePeekBar;
